import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Typography, Table, Tag, Button, Space, Switch, Input, Modal, Form, DatePicker, Select, Drawer, Card,
  Popconfirm, Tooltip, Tabs, Descriptions, List, Avatar, Empty, Segmented, message,
} from 'antd';
import {
  PlusOutlined, EditOutlined, BranchesOutlined, DeleteOutlined, LinkOutlined, DownloadOutlined, EyeOutlined,
  MinusCircleOutlined, UserOutlined, UserSwitchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import api from '../api';
import { UserContext } from '../App';
import { formatTS } from '../utils/time';
import LinksFormList from '../components/LinksFormList';
import { LinksView, linksToForm, linksFromForm } from '../components/ProjectParts';
import CommentBox, { CommentAttachments } from '../components/CommentBox';
import LinkifiedText from '../components/LinkifiedText';

const { RangePicker } = DatePicker;

// Campaign Updates (Oversight > Projects & Tasks): an update -- date, topic,
// description, links to its documents -- sent to teams, acknowledged by the
// people on them from their My Summary To Do. Versions of one update (#1,
// #2 ...) are each their own row; an update's panel lists the others under
// Related Updates.
//
// Access: CanEditCampaignUpdates 1 View, 2 Edit (create, edit and comment on
// the ones you can see), 3 Edit Manager (edit any; delete one nobody has
// acknowledged). The server applies all of it, filters included; this only
// hides buttons.

const today = () => dayjs().format('YYYY-MM-DD');

// Draft: being written, on nobody's To Do. Active: on the To Do (and later
// what notifies people). Inactive: kept, on nobody's To Do.
// Canceled: published by mistake. Once published, never back to Draft.
const STATUSES = ['Draft', 'Active', 'Inactive', 'Canceled'];
const STATUS_COLOR = { Draft: 'gold', Active: 'green', Inactive: 'default', Canceled: 'red' };
// Acknowledgement Required = No: kept for reference (the old Google Sheet ones),
// never on a To Do.
const needsAck = (r) => !!r.AcknowledgementRequired;

// Comments on one update, newest first. Edit (2) and above add them.
function UpdateComments({ updateId, canAdd }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/campaign-updates/${updateId}/comments`)
      .then((r) => setRows(r.data || [])).catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [updateId]);
  useEffect(() => { load(); }, [load]);
  const add = async (text, files) => {
    setSaving(true);
    try {
      const res = await api.post(`/api/campaign-updates/${updateId}/comments`, { comment: text });
      for (const f of files) {
        const fd = new FormData();
        fd.append('comment_type', 'campaign_update');
        fd.append('comment_id', String(res.data.id));
        fd.append('file', f.file, f.name);
        await api.post('/api/attachments', fd);
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not add the comment.');
    } finally {
      setSaving(false);
      load();
    }
  };
  return (
    <>
      {canAdd && <div style={{ marginBottom: 16 }}><CommentBox onSubmit={add} loading={saving} /></div>}
      {!loading && !rows.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No comments yet" /> : (
        <List loading={loading} dataSource={rows} rowKey="ID"
          renderItem={(c) => (
            <List.Item style={{ alignItems: 'flex-start', padding: '8px 0' }}>
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} size="small" />}
                title={(
                  <Space size={8}>
                    <Typography.Text strong style={{ fontSize: 13 }}>{c.UserName}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTS(c.CreatedAt)}</Typography.Text>
                  </Space>
                )}
                description={(
                  <>
                    <Typography.Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}><LinkifiedText text={c.Comment} /></Typography.Text>
                    <CommentAttachments attachments={c.Attachments} />
                  </>
                )}
              />
            </List.Item>
          )} />
      )}
    </>
  );
}

export default function CampaignUpdatesPage() {
  const currentUser = useContext(UserContext);
  const [rows, setRows] = useState([]);
  const [level, setLevel] = useState(0);
  const [loading, setLoading] = useState(false);
  // Filters. Teams offered are your Visible Teams; the server also ignores
  // any other.
  const [fStatus, setFStatus] = useState(['Draft', 'Active']);
  const [fTeams, setFTeams] = useState([]);
  // Blank = not filtered.
  const [fReq, setFReq] = useState(null);
  const [fAck, setFAck] = useState(null);
  const [fSources, setFSources] = useState([]);
  const [fRange, setFRange] = useState(null);
  const [search, setSearch] = useState('');
  const [teams, setTeams] = useState([]);
  const [people, setPeople] = useState([]);
  // Assigned To: people with Edit or above, plus whoever an update already has.
  const [assignees, setAssignees] = useState([]);
  // Sources (Admin > Campaign Updates Config): every one for the filter (old
  // updates keep an inactive source); the form offers the active ones, plus
  // the update's own if it has since been made inactive.
  const [sources, setSources] = useState([]);
  const [formSources, setFormSources] = useState([]);
  // WFM Skills picker: active skills and their teams (WFM Skills Config).
  const [skillOpts, setSkillOpts] = useState([]);
  useEffect(() => {
    api.get('/api/campaign-update-sources', { params: { inactive: 1 } }).then((r) => setSources(r.data || [])).catch(() => setSources([]));
  }, []);
  const [editing, setEditing] = useState(null);     // { mode: 'new' | 'edit' | 'version', source? }
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);       // { id, tab }
  const [info, setInfo] = useState(null);           // the open update's details
  const [acks, setAcks] = useState(null);           // its acknowledgements
  const [notYetOnly, setNotYetOnly] = useState(false);
  // Link to Update (New Version was forgotten): the updates it can join.
  const [linkOpts, setLinkOpts] = useState(null);
  const [linkTo, setLinkTo] = useState(null);
  const [linking, setLinking] = useState(false);
  const [form] = Form.useForm();
  // The form's People list: who must acknowledge as it stands. A published
  // update uses its saved list (its teams are locked); otherwise the teams'
  // active members now, narrowed by the WFM skills picked. Then Include /
  // Exclude.
  const wTeams = Form.useWatch('team_ids', form);
  const wSkills = Form.useWatch('skill_ids', form);
  const wMatch = Form.useWatch('skill_match', form);
  const wParts = Form.useWatch('participants', form);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!editing) { setPreview(null); return undefined; }
    const t = setTimeout(() => {
      api.post('/api/campaign-updates/preview', {
        update_id: editing.mode === 'edit' ? editing.source?.ID : null,
        team_ids: wTeams || [],
        skill_ids: wSkills || [],
        skill_match: wMatch || 'any',
        participants: (wParts || []).filter((x) => x && x.user_id && x.type),
      }).then((r) => setPreview(r.data || [])).catch(() => setPreview(null));
    }, 300);
    return () => clearTimeout(t);
  }, [editing, wTeams, wSkills, wMatch, wParts]);

  // The x on a People tag: someone Included loses that row; anyone else
  // gets an Exclude row (removing the row takes it back). The preview then
  // reloads without them.
  const dropPerson = (x) => {
    const parts = form.getFieldValue('participants') || [];
    const next = x.Via === 'include'
      ? parts.filter((r) => !(r && r.type === 'include' && r.user_id === x.UserID))
      : [...parts.filter((r) => !(r && r.user_id === x.UserID)), { type: 'exclude', user_id: x.UserID }];
    form.setFieldValue('participants', next);
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = {
      status: fStatus.length ? fStatus.join(',') : 'all',
      ...(fTeams.length ? { team_ids: fTeams.join(',') } : {}),
      ...(fReq ? { ack_required: fReq } : {}),
      ...(fAck ? { ack: fAck } : {}),
      ...(fSources.length ? { source_ids: fSources.join(',') } : {}),
      ...(fRange?.[0] ? { from: fRange[0].format('YYYY-MM-DD') } : {}),
      ...(fRange?.[1] ? { to: fRange[1].format('YYYY-MM-DD') } : {}),
    };
    api.get('/api/campaign-updates', { params })
      .then((r) => { setRows(r.data.rows || []); setLevel(r.data.level || 0); })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [fStatus, fTeams, fReq, fAck, fSources, fRange]);
  useEffect(() => { load(); }, [load]);

  const canEdit = level >= 2;
  const canDelete = level >= 3;

  // Your Visible Teams (every team for admins and when none are set) -- the
  // Team filter, and what Edit can pick.
  useEffect(() => {
    api.get('/api/teams', { params: { my_teams: 1 } }).then((r) => setTeams(r.data || [])).catch(() => setTeams([]));
  }, []);
  useEffect(() => {
    if (!canEdit) return;
    // Edit Manager picks from every team.
    if (level >= 3 && currentUser?.Role !== 'admin') {
      api.get('/api/teams').then((r) => setTeams(r.data || [])).catch(() => {});
    }
    api.get('/api/campaign-updates/people').then((r) => setPeople(r.data || [])).catch(() => setPeople([]));
  }, [canEdit, level, currentUser]);

  // Every version is its own row (the server's order: newest Date of Update
  // first); the search narrows by topic and description.
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => `${r.Title} ${r.Description || ''}`.toLowerCase().includes(q)) : rows;
  }, [rows, search]);

  // -- the update panel ---------------------------------------------------------
  // Edit Manager, Active update: acknowledge for someone, recorded as such.
  const ackFor = (person) => api.post(`/api/campaign-updates/${detail.id}/acknowledge-for`, { user_id: person.UserID })
    .then(() => {
      message.success(`Acknowledged on behalf of ${person.DisplayName}.`);
      api.get(`/api/campaign-updates/${detail.id}/acknowledgements`).then((res) => setAcks(res.data)).catch(() => {});
      load();
    })
    .catch((err) => message.error(err.response?.data?.error || 'Could not acknowledge.'));

  const openDetail = (r, tab = 'details') => {
    setLinkOpts(null); setLinkTo(null);
    setDetail({ id: r.ID, tab });
    setInfo(null); setAcks(null); setNotYetOnly(false);
    api.get(`/api/campaign-updates/${r.ID}`).then((res) => setInfo(res.data)).catch(() => message.error('Could not load the update.'));
    api.get(`/api/campaign-updates/${r.ID}/acknowledgements`).then((res) => setAcks(res.data)).catch(() => setAcks({ rows: [] }));
  };
  const ackRows = (acks?.rows || []).filter((r) => !notYetOnly || (r.Required && !r.AcknowledgedAt));
  const exportAcks = () => {
    const data = (acks?.rows || []).map((r) => ({
      Name: r.DisplayName, Team: r.TeamName || '',
      Via: r.Via === 'include' ? 'Included' : r.Via === 'team' ? 'Team' : 'No longer on it',
      Acknowledged: r.AcknowledgedAt ? formatTS(r.AcknowledgedAt) : 'Not yet',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'Acknowledgements');
    XLSX.writeFile(wb, `campaign-update-${detail.id}-acknowledgements.xlsx`);
  };

  // -- create / edit / new version ---------------------------------------------
  const openForm = async (mode, source) => {
    let d = null;
    if (source) {
      try { d = (await api.get(`/api/campaign-updates/${source.ID}`)).data; } catch (_) { message.error('Could not load the update.'); return; }
    }
    api.get('/api/campaign-update-sources', { params: d?.SourceID ? { current: d.SourceID } : {} })
      .then((r) => setFormSources(r.data || [])).catch(() => setFormSources([]));
    api.get('/api/campaign-updates/skill-options', { params: d?.SkillIDs?.length ? { current: d.SkillIDs.join(',') } : {} })
      .then((r) => setSkillOpts(r.data || [])).catch(() => setSkillOpts([]));
    api.get('/api/campaign-updates/assignees', { params: d?.AssignedToUserID ? { current: d.AssignedToUserID } : {} })
      .then((r) => setAssignees(r.data || [])).catch(() => setAssignees([]));
    const shared = d ? {
      title: d.Title, description: d.Description || '', links: linksToForm(d.Links),
      assigned_to: d.AssignedToUserID ?? null, source_id: d.SourceID ?? null, requestor: d.Requestor || '',
      team_ids: d.TeamIDs, skill_ids: d.SkillIDs || [], skill_match: d.SkillMatch || 'any',
      ack_required: !!d.AcknowledgementRequired,
      participants: (d.Participants || []).map((p) => ({ user_id: p.UserID, type: p.Type })),
    } : {};
    form.resetFields();
    form.setFieldsValue(mode === 'edit' ? {
      ...shared, update_date: d.UpdateDate ? dayjs(d.UpdateDate) : null,
      ack_due_date: d.AckDueDate ? dayjs(d.AckDueDate) : null, status: d.Status,
      updated_in_training: !!d.UpdatedInTraining,
    } : mode === 'version' ? {
      // A new version starts from the one it follows; the date is today's.
      // A new version is new material: not yet in training.
      ...shared, update_date: dayjs(), ack_due_date: null, status: 'Draft', updated_in_training: false,
    } : { update_date: dayjs(), status: 'Draft', ack_required: true, updated_in_training: false, assigned_to: null, source_id: null, requestor: '', team_ids: [], skill_ids: [], skill_match: 'any', participants: [], links: [{}] });
    setEditing({ mode, source: d });
  };

  const save = async () => {
    const v = await form.validateFields();
    const body = {
      update_date: v.update_date ? v.update_date.format('YYYY-MM-DD') : null,
      title: v.title, description: v.description || null, links: linksFromForm(v.links),
      ack_due_date: v.ack_due_date ? v.ack_due_date.format('YYYY-MM-DD') : null,
      team_ids: v.team_ids || [], skill_ids: v.skill_ids || [], skill_match: v.skill_match || 'any',
      status: v.status, ack_required: !!v.ack_required,
      updated_in_training: !!v.updated_in_training, assigned_to: v.assigned_to ?? null,
      source_id: v.source_id ?? null, requestor: (v.requestor || '').trim() || null,
      participants: (v.participants || []).filter((p) => p && p.user_id && p.type),
    };
    setSaving(true);
    try {
      if (editing.mode === 'edit') {
        await api.put(`/api/campaign-updates/${editing.source.ID}`, body);
        message.success('Update saved.');
      } else {
        const r = await api.post('/api/campaign-updates', {
          ...body, ...(editing.mode === 'version' ? { new_version_of: editing.source.ID } : {}),
        });
        message.success(editing.mode === 'version' ? 'New version created.' : 'Update created.');
      }
      setEditing(null);
      load();
      if (detail) openDetail({ ID: detail.id }, detail.tab);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/api/campaign-updates/${id}`);
      message.success('Update deleted.');
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to delete.');
    }
  };

  const overdue = (r) => needsAck(r) && r.Status === 'Active' && r.AckDueDate && r.AckDueDate < today() && r.Acknowledged < r.Expected;

  const columns = [
    // The buttons first: View (everyone here has at least View access; the
    // topic opens the same panel), then Edit, New Version, Delete as allowed.
    {
      title: '', key: 'actions', width: canEdit ? 145 : 50, fixed: 'left',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)} /></Tooltip>
          {canEdit && (
            <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => openForm('edit', r)} /></Tooltip>
          )}
          {canEdit && (
            <Tooltip title="New Version"><Button size="small" icon={<BranchesOutlined />} onClick={() => openForm('version', r)} /></Tooltip>
          )}
          {canDelete && r.TotalAcks === 0 && (
            <Popconfirm title="Delete this update?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove(r.ID)}>
              <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} /></Tooltip>
            </Popconfirm>
          )}
        </Space>
      ) },
    { title: 'Date of Update', dataIndex: 'UpdateDate', key: 'UpdateDate', width: 120,
      sorter: (a, b) => (a.UpdateDate || '').localeCompare(b.UpdateDate || '') },
    // Narrow, and wraps: a long topic runs onto more lines, the link icons
    // after its last word.
    { title: 'Topic', dataIndex: 'Title', key: 'Title', width: 280,
      sorter: (a, b) => (a.Title || '').localeCompare(b.Title || ''),
      render: (v, r) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          <a onClick={() => openDetail(r)}>{v}</a>
          {(r.Links || []).map((l) => (
            <Tooltip key={l.ID} title={l.Label || l.URL}>
              <a href={l.URL} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 6 }}><LinkOutlined /></a>
            </Tooltip>
          ))}
          {r.Description && (
            <Typography.Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ fontSize: 12, margin: 0 }}>
              {r.Description}
            </Typography.Paragraph>
          )}
        </div>
      ) },
    { title: 'Status', dataIndex: 'Status', key: 'Status', width: 90,
      sorter: (a, b) => STATUSES.indexOf(a.Status) - STATUSES.indexOf(b.Status),
      render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    { title: 'Updated in Training', dataIndex: 'UpdatedInTraining', key: 'UpdatedInTraining', width: 150,
      sorter: (a, b) => Number(a.UpdatedInTraining) - Number(b.UpdatedInTraining),
      render: (v) => (v ? 'Yes' : 'No') },
    { title: 'Source', dataIndex: 'SourceName', key: 'SourceName', width: 130,
      sorter: (a, b) => (a.SourceName || '~').localeCompare(b.SourceName || '~'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Requestor', dataIndex: 'Requestor', key: 'Requestor', width: 130, ellipsis: true,
      sorter: (a, b) => (a.Requestor || '~').localeCompare(b.Requestor || '~'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Teams', dataIndex: 'Teams', key: 'Teams', width: 120,
      render: (ts) => <Space size={4} wrap>{(ts || []).map((t) => <Tag key={t.ID}>{t.Name}</Tag>)}</Space> },
    { title: 'Ack Due', dataIndex: 'AckDueDate', key: 'AckDueDate', width: 110,
      sorter: (a, b) => (a.AckDueDate || '').localeCompare(b.AckDueDate || ''),
      render: (v, r) => (v ? <span style={{ color: overdue(r) ? '#ff4d4f' : undefined }}>{v}</span>
        : <Typography.Text type="secondary">—</Typography.Text>) },
    // Who it is for. Opens the Acknowledgements list.
    { title: 'People', dataIndex: 'Expected', key: 'Expected', width: 80, align: 'right',
      sorter: (a, b) => a.Expected - b.Expected,
      render: (v, r) => <a onClick={() => openDetail(r, 'acks')}>{v}</a> },
    // Done - pending (e.g. 18 - 5), or Not required. Pending is orange, red
    // once the due date has passed; 0 pending is green. Opens the
    // Acknowledgements list (Not yet only while some are pending). Sorts by
    // how many are pending.
    { title: 'Ack Done vs Pending', key: 'AckDonePending', width: 160, align: 'center',
      sorter: (a, b) => (needsAck(a) ? a.Expected - a.Acknowledged : -1) - (needsAck(b) ? b.Expected - b.Acknowledged : -1),
      render: (_, r) => {
        if (!needsAck(r)) return <Typography.Text type="secondary">Not required</Typography.Text>;
        const pending = r.Expected - r.Acknowledged;
        return (
          <a onClick={() => { openDetail(r, 'acks'); if (pending > 0) setNotYetOnly(true); }}>
            <span style={{ color: '#52c41a', fontWeight: 600 }}>{r.Acknowledged}</span>
            <span style={{ color: 'inherit' }}> - </span>
            <span style={{ color: pending <= 0 ? '#52c41a' : overdue(r) ? '#ff4d4f' : '#fa8c16', fontWeight: 600 }}>{pending}</span>
          </a>
        );
      } },
    { title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 150,
      sorter: (a, b) => (a.AssignedToName || '~').localeCompare(b.AssignedToName || '~'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
  ];

  // Teams already on the update that you cannot pick stay on it.
  const teamOptions = useMemo(() => {
    const opts = teams.map((t) => ({ value: t.ID, label: t.Name }));
    const extra = (editing?.mode === 'edit' ? editing?.source?.Teams || [] : [])
      .filter((t) => !teams.some((x) => x.ID === t.ID))
      .map((t) => ({ value: t.ID, label: t.Name, disabled: true }));
    return [...opts, ...extra];
  }, [teams, editing]);
  const published = editing?.mode === 'edit' && editing?.source?.Status && editing.source.Status !== 'Draft';
  // The WFM skills of the picked teams (and any the update already has).
  const keptSkills = editing?.source?.SkillIDs || [];
  const skillOptions = skillOpts
    .filter((s) => keptSkills.includes(s.ID) || (s.IsActive && s.TeamIDs.some((t) => (wTeams || []).includes(t))))
    .map((s) => ({ value: s.ID, label: s.IsActive ? s.Name : `${s.Name} (inactive)` }));
  const personOptions = people.map((p) => ({ value: p.ID, label: p.TeamName ? `${p.DisplayName} (${p.TeamName})` : p.DisplayName }));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Campaign Updates</Typography.Title>
        {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm('new')}>New Update</Button>}
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search allowClear placeholder="Search topics and descriptions" style={{ width: 260 }}
          onChange={(e) => setSearch(e.target.value)} />
        <Select mode="multiple" allowClear placeholder="All statuses" style={{ minWidth: 200 }}
          value={fStatus} onChange={setFStatus} options={STATUSES.map((v) => ({ value: v, label: v }))} />
        <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="All teams" style={{ minWidth: 220 }}
          maxTagCount="responsive" value={fTeams} onChange={setFTeams}
          options={teams.map((t) => ({ value: t.ID, label: t.Name }))} />
        <Select allowClear placeholder="Acknowledgement Required" style={{ width: 240 }} value={fReq}
          onChange={(v) => setFReq(v ?? null)}
          options={[{ value: 'yes', label: 'Acknowledgement Required: Yes' }, { value: 'no', label: 'Acknowledgement Required: No' }]} />
        <Select allowClear placeholder="Acknowledged" style={{ width: 240 }} value={fAck}
          onChange={(v) => setFAck(v ?? null)}
          options={[{ value: 'outstanding', label: 'Acknowledged: Not Complete' }, { value: 'complete', label: 'Acknowledged: Completed' }]} />
        <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Source" style={{ minWidth: 160 }}
          maxTagCount="responsive" value={fSources} onChange={setFSources}
          options={sources.map((x) => ({ value: x.ID, label: x.IsActive ? x.Name : `${x.Name} (inactive)` }))} />
        <RangePicker value={fRange} onChange={setFRange} placeholder={['Date of Update from', 'to']} />
      </Space>

      <Table rowKey="ID" size="small" loading={loading} dataSource={shown} columns={columns}
        // Column headers stay at the top while scrolling (under the Harbor bar
        // in that look -- HarborColors.css moves a 0 offset down for it).
        sticky={{ offsetHeader: 0 }}
        // The pager always shows (with its page-size picker): hiding it on a
        // single page also hid the picker once a bigger size was chosen.
        pagination={{ defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '200'],
          showTotal: (t) => `${t} update${t === 1 ? '' : 's'}` }} scroll={{ x: 'max-content' }} />

      {/* One update: its details, who has acknowledged it, and comments. */}
      <Drawer width={680} open={!!detail} onClose={() => setDetail(null)} destroyOnClose
        title={info ? info.Title : 'Campaign Update'}
        extra={detail?.tab === 'acks' && <Button icon={<DownloadOutlined />} onClick={exportAcks} disabled={!acks?.rows?.length}>Export</Button>}>
        {detail && (
          <Tabs activeKey={detail.tab} onChange={(tab) => setDetail((d) => ({ ...d, tab }))} items={[
            { key: 'details', label: 'Details', children: info && (
              <>
                {info.Description && (
                  <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}><LinkifiedText text={info.Description} /></Typography.Paragraph>
                )}
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="Links">
                    {info.Links?.length ? <LinksView links={info.Links} /> : <Typography.Text type="secondary">—</Typography.Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date of Update">{info.UpdateDate}</Descriptions.Item>
                  <Descriptions.Item label="Source">{info.SourceName || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Requestor">{info.Requestor || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Assigned To">{info.AssignedToName || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Updated in Training">{info.UpdatedInTraining ? 'Yes' : 'No'}</Descriptions.Item>
                  <Descriptions.Item label="Teams">
                    <Space size={4} wrap>{(info.Teams || []).map((t) => <Tag key={t.ID}>{t.Name}</Tag>)}</Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="WFM Skills">
                    {info.Skills?.length ? (
                      <Space size={4} wrap>
                        {info.Skills.map((s) => <Tag key={s.ID}>{s.Name}{s.IsActive ? '' : ' (inactive)'}</Tag>)}
                        {info.Skills.length > 1 && (
                          <Typography.Text type="secondary">({info.SkillMatch === 'all' ? 'All' : 'Any'})</Typography.Text>
                        )}
                      </Space>
                    ) : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Additional People">
                    {info.Participants?.length ? (
                      <Space size={4} wrap>
                        {info.Participants.map((p) => (
                          <Tag key={p.UserID} color={p.Type === 'include' ? 'blue' : 'default'}>
                            {p.Type === 'include' ? 'Include' : 'Exclude'}: {p.DisplayName}
                          </Tag>
                        ))}
                      </Space>
                    ) : <Typography.Text type="secondary">—</Typography.Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Acknowledgement Required">{info.AcknowledgementRequired ? 'Yes' : 'No'}</Descriptions.Item>
                  <Descriptions.Item label="Acknowledge Due Date">{info.AckDueDate || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Status"><Tag color={STATUS_COLOR[info.Status]}>{info.Status}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Created">{info.CreatedByName || '—'} · {formatTS(info.CreatedAt)}</Descriptions.Item>
                </Descriptions>
                {/* Comments under the details, so they are seen without a
                    separate tab to check. */}
                <Typography.Title level={5} style={{ marginTop: 24 }}>Comments</Typography.Title>
                <UpdateComments updateId={detail.id} canAdd={canEdit} />
              </>
            ) },
            // "Acknowledgements (X of Y)": acknowledged of those who must; plain
            // while loading, and when acknowledgement is not required.
            { key: 'acks', label: (() => {
              if (!acks?.update || !acks.update.AcknowledgementRequired) return 'Acknowledgements';
              const req = (acks.rows || []).filter((r) => r.Required);
              return `Acknowledgements (${req.filter((r) => r.AcknowledgedAt).length} of ${req.length})`;
            })(), children: (
              <>
                <Space size={6} style={{ marginBottom: 12 }}>
                  <Switch size="small" checked={notYetOnly} onChange={setNotYetOnly} /><span>Not yet only</span>
                </Space>
                <Table rowKey="UserID" size="small" loading={!acks} dataSource={ackRows}
                  pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['50', '100', '200', '500'],
                    showTotal: (t) => `${t} ${t === 1 ? 'person' : 'people'}` }}
                  columns={[
                    { title: 'Name', dataIndex: 'DisplayName', key: 'DisplayName',
                      sorter: (a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || ''),
                      render: (v, r) => <>{v}{r.Left && <Tag style={{ marginLeft: 6 }}>Left</Tag>}</> },
                    { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', render: (v) => v || '—' },
                    { title: 'Via', dataIndex: 'Via', key: 'Via', width: 90,
                      render: (v) => (v === 'include' ? 'Included' : v === 'team' ? 'Team'
                        : <Typography.Text type="secondary">No longer on it</Typography.Text>) },
                    { title: 'Acknowledged', dataIndex: 'AcknowledgedAt', key: 'AcknowledgedAt', width: 180,
                      // Someone who has left is not pending.
                      render: (v, r) => (v ? (
                        <>
                          {formatTS(v)}
                          {r.AcknowledgedByName && (
                            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                              on behalf, by {r.AcknowledgedByName}
                            </Typography.Text>
                          )}
                        </>
                      ) : r.Left ? <Typography.Text type="secondary">—</Typography.Text>
                        : <span style={{ color: acks?.update?.AckDueDate && acks.update.AckDueDate < today() ? '#ff4d4f' : '#fa8c16' }}>Not yet</span>) },
                    // Edit Manager on an Active update: acknowledge for someone.
                    ...(acks?.canAckOnBehalf ? [{ title: '', key: 'onBehalf', width: 200, align: 'right',
                      render: (_, r) => r.Required && !r.AcknowledgedAt && !r.Left && (
                        <Popconfirm title={`Acknowledge on behalf of ${r.DisplayName}?`}
                          description="It is recorded as acknowledged by you on their behalf."
                          okText="Acknowledge" onConfirm={() => ackFor(r)}>
                          <Button size="small" icon={<UserSwitchOutlined />}>Acknowledge on behalf</Button>
                        </Popconfirm>
                      ) }] : []),
                  ]} />
              </>
            ) },
            // The other versions of this update, newest Date of Update first; click one to open it.
            { key: 'related', label: `Related Updates${info?.Related ? ` (${info.Related.length})` : ''}`, children: (
              <>
              {/* Not in a chain yet: link it to the update it should have been
                  a new version of. Versions run in Date of Update order. */}
              {canEdit && info && !info.Related?.length && (
                <div style={{ marginBottom: 12 }}>
                  {linkOpts === null ? (
                    <Button icon={<BranchesOutlined />} onClick={() => {
                      setLinkTo(null);
                      api.get(`/api/campaign-updates/${detail.id}/link-options`)
                        .then((r) => setLinkOpts(r.data || [])).catch(() => message.error('Could not load the updates.'));
                    }}>Link to Update</Button>
                  ) : (
                    <Space wrap>
                      <Select showSearch optionFilterProp="label" placeholder="The update this is a new version of"
                        style={{ width: 420 }} value={linkTo?.ID} onChange={(v) => setLinkTo(linkOpts.find((o) => o.ID === v))}
                        options={linkOpts.map((o) => ({ value: o.ID, label: `${o.Title} · ${o.UpdateDate || ''} · ${o.Status}` }))} />
                      <Popconfirm disabled={!linkTo}
                        title={linkTo ? `Link this update as a version of "${linkTo.Title}" (${linkTo.UpdateDate || ''})?` : ''}
                        okText="Link" onConfirm={() => {
                          setLinking(true);
                          api.post(`/api/campaign-updates/${detail.id}/link`, { target_id: linkTo.ID })
                            .then((r) => {
                              message.success('Linked.');
                              setLinkOpts(null); setLinkTo(null);
                              openDetail({ ID: detail.id }, 'related');
                              load();
                            })
                            .catch((err) => message.error(err.response?.data?.error || 'Could not link it.'))
                            .finally(() => setLinking(false));
                        }}>
                        <Button type="primary" disabled={!linkTo} loading={linking}>Link</Button>
                      </Popconfirm>
                      <Button onClick={() => { setLinkOpts(null); setLinkTo(null); }}>Cancel</Button>
                    </Space>
                  )}
                </div>
              )}
              <Table rowKey="ID" size="small" loading={!info} dataSource={info?.Related || []} pagination={false}
                locale={{ emptyText: 'No other versions of this update.' }}
                onRow={(r) => ({ onClick: () => openDetail(r), style: { cursor: 'pointer' } })}
                columns={[
                  { title: 'Date of Update', dataIndex: 'UpdateDate', key: 'UpdateDate', width: 120 },
                  { title: 'Topic', dataIndex: 'Title', key: 'Title', render: (v) => <a>{v}</a> },
                  { title: 'Status', dataIndex: 'Status', key: 'Status', width: 90,
                    render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
                ]} />
              </>
            ) },
          ]} />
        )}
      </Drawer>

      <Modal maskClosable={false} width={860} open={!!editing} onCancel={() => setEditing(null)} onOk={save}
        confirmLoading={saving} okText={editing?.mode === 'edit' ? 'Save' : 'Create'}
        title={editing?.mode === 'edit' ? 'Edit Campaign Update'
          : editing?.mode === 'version' ? `New Version of "${editing?.source?.Title}"` : 'New Campaign Update'}>
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Space size={12} style={{ display: 'flex' }} align="start">
            <Form.Item name="update_date" label="Date of Update" rules={[{ required: true, message: 'Pick a date' }]}>
              <DatePicker />
            </Form.Item>
            <Form.Item name="ack_due_date" label="Acknowledge Due Date">
              <DatePicker />
            </Form.Item>
          </Space>
          <Form.Item name="title" label="Topic" rules={[{ required: true, message: 'Enter a topic' }]}>
            <Input maxLength={300} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} />
          </Form.Item>
          <Form.Item label="Links"><LinksFormList name="links" /></Form.Item>
          <Space size={12} style={{ display: 'flex' }} align="start">
            <Form.Item name="assigned_to" label="Assigned To">
              <Select allowClear showSearch optionFilterProp="label" style={{ width: 300 }}
                options={assignees.map((u) => ({
                  value: u.ID,
                  // Already on the update but no longer pickable: kept, and said why.
                  label: u.Current ? `${u.DisplayName} (${u.IsActive ? 'no longer has access' : 'inactive'})` : u.DisplayName,
                }))} />
            </Form.Item>
            <Form.Item name="source_id" label="Source">
              <Select allowClear showSearch optionFilterProp="label" style={{ width: 200 }}
                options={formSources.map((x) => ({ value: x.ID, label: x.IsActive ? x.Name : `${x.Name} (inactive)` }))} />
            </Form.Item>
          </Space>
          <Space size={12} style={{ display: 'flex' }} align="start">
            <Form.Item name="requestor" label="Requestor">
              <Input maxLength={50} style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="updated_in_training" label="Updated in Training">
              <Select style={{ width: 120 }} options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} />
            </Form.Item>
          </Space>
          {/* Who it is for: the teams, narrowed by WFM skills -- one box, as
              they work together. Locked once published: who must acknowledge
              was saved then. */}
          <Card size="small" style={{ marginBottom: 16 }}>
          <Form.Item name="team_ids" label="Which team(s) can see this?" rules={[{ required: true, message: 'Pick at least one team' }]}
            extra={published ? 'Locked: the people on these teams were saved when the update was published.' : null}>
            <Select mode="multiple" showSearch optionFilterProp="label" options={teamOptions} disabled={published}
              onChange={(teams) => {
                // Drop skills that no longer belong to a picked team.
                const keep = new Set(skillOpts.filter((s) => s.TeamIDs.some((t) => teams.includes(t))).map((s) => s.ID));
                form.setFieldValue('skill_ids', (form.getFieldValue('skill_ids') || [])
                  .filter((id) => keep.has(id) || keptSkills.includes(id)));
              }} />
          </Form.Item>
          {/* Optional: only people on the teams who have these skills. */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Form.Item name="skill_ids" label="Skills - Who should acknowledgement be restricted to (optional)?" style={{ flex: '1 1 320px', marginBottom: 0 }}>
              <Select mode="multiple" allowClear showSearch optionFilterProp="label"
                options={skillOptions} disabled={published} />
            </Form.Item>
            {(wSkills || []).length > 1 && (
              <Form.Item name="skill_match" label="Skills Match" style={{ marginBottom: 0 }}>
                <Segmented disabled={published} options={[{ value: 'any', label: 'Any' }, { value: 'all', label: 'All' }]} />
              </Form.Item>
            )}
          </div>
          </Card>
          <Space size={24}>
            <Form.Item name="ack_required" label="Acknowledgement Required">
              <Select style={{ width: 120 }} options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} />
            </Form.Item>
            <Form.Item name="status" label="Status" rules={[{ required: true }]}>
              <Select style={{ width: 140 }}
                options={STATUSES.filter((v) => !(published && v === 'Draft')).map((v) => ({ value: v, label: v }))} />
            </Form.Item>
          </Space>
          <Typography.Text strong style={{ display: 'block' }}>Additional People</Typography.Text>
          <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
            Include - add someone who isn't on the selected team(s) to the acknowledgement list.
            Exclude - do not require someone to acknowledge.
          </Typography.Text>
          <Form.List name="participants">
            {(fields, { add, remove: drop }) => (
              <>
                {fields.map((f) => (
                  <Space key={f.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item name={[f.name, 'type']} rules={[{ required: true, message: 'Pick one' }]} style={{ marginBottom: 0 }}>
                      <Select style={{ width: 110 }} options={[{ value: 'include', label: 'Include' }, { value: 'exclude', label: 'Exclude' }]} />
                    </Form.Item>
                    <Form.Item name={[f.name, 'user_id']} rules={[{ required: true, message: 'Pick a person' }]} style={{ marginBottom: 0 }}>
                      <Select showSearch optionFilterProp="label" style={{ width: 360 }} options={personOptions} />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => drop(f.name)} />
                  </Space>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ type: 'include' })}>Add Person</Button>
              </>
            )}
          </Form.List>
          <Typography.Text strong style={{ display: 'block', marginTop: 16 }}>
            People{preview ? ` (${preview.filter((x) => !x.Left).length})` : ''}
          </Typography.Text>
          <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 6 }}>
            {preview === null ? <Typography.Text type="secondary">—</Typography.Text>
              : !preview.length ? <Typography.Text type="secondary">Nobody yet: pick a team or include someone.</Typography.Text>
              : (
                <Space size={[4, 4]} wrap>
                  {preview.map((x) => (
                    <Tag key={x.UserID} color={x.Left ? 'default' : x.Via === 'include' ? 'blue' : undefined}
                      closable={!x.Left} onClose={(e) => { e.preventDefault(); dropPerson(x); }}>
                      {x.DisplayName}{x.TeamName ? ` (${x.TeamName})` : ''}{x.Via === 'include' ? ' · Included' : ''}{x.Left ? ' · Left' : ''}
                    </Tag>
                  ))}
                </Space>
              )}
          </div>
        </Form>
      </Modal>
    </>
  );
}
